import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
} from "reactstrap";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import {
  updateEmployeeByEmployerId,
  updateIndividualById,
} from "../../../slices/general/thunk";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { states } from "../../../utils/utils";
import Cleave from "cleave.js/react";

const EditEmployerModal = ({
  open,
  setOpen,
  editUser,
  setUser,
  onRefreshTable,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const toggleModalEditUser = () => {
    setOpen(!open);
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    companyName: Yup.string().required("Company Name is required"),
    companyAddress: Yup.string().required("Company Address is required"),
    zipCode: Yup.string()
      .matches(/^[0-9]{1,5}$/, "Zip Code is not valid")
      .required("Zip Code is required"),
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone Number must be 10 digits")
      .required("Phone Number is required"),

    state: Yup.string().required("State is required"),
  });

  const handleEditUser = async (values) => {
    setLoading(true);
    try {
      const actionToDispatch = editUser.individualCode
        ? updateIndividualById({
          individualId: editUser.id,
          data: values,
        })
        : updateEmployeeByEmployerId({
          employerId: editUser.employerId,
          employeeId: editUser.id,
          data: values,
        });

      const response = await dispatch(actionToDispatch).unwrap();

      if (!response.error) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `${editUser.individualCode ? "Individual" : "Employee"
            } updated successfully`,
        });
        toggleModalEditUser();
        onRefreshTable();
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: response.error.message,
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={open} toggle={toggleModalEditUser}>
      <ModalHeader toggle={toggleModalEditUser}>
        Edit Employer Group
      </ModalHeader>
      <ModalBody>
        <Formik
          initialValues={{
            firstName: editUser.firstName,
            lastName: editUser.lastName,
            email: editUser.email,
            companyAddress: editUser.companyAddress,
            companyName: editUser.companyName,
            phoneNumber: editUser.phoneNumber,
            zipCode: editUser.zipCode,
            state: editUser.state,
          }}
          validationSchema={validationSchema}
          onSubmit={handleEditUser}
        >
          {({ isSubmitting, dirty, isValid }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      Email
                    </label>
                    <Field
                      type="email"
                      name="email"
                      className="form-control"
                      disabled
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="mb-3">
                    <label htmlFor="phoneNumber" className="form-label">
                      Phone Number
                    </label>
                    <Field name="phoneNumber">
                      {({ field, form, meta }) => (
                        <Cleave
                          {...field}
                          className="form-control"
                          options={{
                            numericOnly: true,
                            delimiters: [, "(", ")-", "-"],
                            blocks: [0, 0, 3, 3, 4],
                            delimiterLazyShow: true,
                          }}
                          onChange={(e) => {
                            form.setFieldValue(
                              "phoneNumber",
                              e.target.rawValue
                            );
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="phoneNumber"
                      component="div"
                      className="text-danger"
                    />
                  </div>{" "}
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <div className="mb-3">
                    <label htmlFor="firstName" className="form-label">
                      First Name
                    </label>
                    <Field
                      type="text"
                      name="firstName"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="text-danger"
                    />
                  </div>{" "}
                </Col>{" "}
                <Col lg={6}>
                  <div className="mb-3">
                    <label htmlFor="lastName" className="form-label">
                      Last Name
                    </label>
                    <Field
                      type="text"
                      name="lastName"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="lastName"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <div className="mb-3">
                    <label htmlFor="companyName" className="form-label">
                      Company Name
                    </label>
                    <Field
                      type="text"
                      name="companyName"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="companyName"
                      component="div"
                      className="text-danger"
                    />
                  </div>{" "}
                </Col>{" "}
                <Col lg={6}>
                  <div className="mb-3">
                    <label htmlFor="companyAddress" className="form-label">
                      Company Address
                    </label>
                    <Field
                      type="text"
                      name="companyAddress"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="companyAddress"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <div className="mb-3">
                    <label htmlFor="zipCode" className="form-label">
                      Zip Code
                    </label>
                    <Field
                      onInput={(e) => {
                        if (e.target.value.length > 5)
                          e.target.value = e.target.value.slice(0, 5);
                      }}
                      type="text"
                      name="zipCode"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="zipCode"
                      component="div"
                      className="text-danger"
                    />
                  </div>{" "}
                </Col>{" "}
                <Col lg={6}>
                  <div className="mb-3">
                    <label htmlFor="state" className="form-label">
                      State
                    </label>
                    <Field as="select" name="state" className="form-control">
                      <option value="" disabled>
                        Select State
                      </option>
                      {states.map((state, index) => (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="companyAddress"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col className="col-6">
                  <Button
                    color="soft-primary"
                    className="border border-primary w-100"
                    onClick={toggleModalEditUser}
                  >
                    Cancel
                  </Button>
                </Col>
                <Col className="col-6">
                  <Button
                    type="submit"
                    color="primary"
                    className="w-100"
                    disabled={isSubmitting || !dirty || !isValid || loading}
                  >
                    {isSubmitting ? (
                      <div className="spinner-border spinner-border-sm mr-3"></div>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default EditEmployerModal;
